import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ComicCard from "@/components/feature/ComicCard";
import { comics } from "@/mocks/comics";

export default function ComicDetail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 text-center">
        <Navbar />
        <i className="ri-error-warning-line text-5xl text-foreground-700"></i>
        <h1 className="mt-4 font-heading text-2xl text-foreground-950">没有找到这部漫画</h1>
        <Link to="/recommend" className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors">
          返回爽漫推荐
        </Link>
      </div>
    );
  }

  const related = comics.filter((c) => c.id !== comic.id && (c.genre === comic.genre)).concat(comics.filter((c) => c.id !== comic.id && c.genre !== comic.genre)).slice(0, 4);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://bencomic.github.io/" },
      { "@type": "ListItem", position: 2, name: comic.genre, item: `https://bencomic.github.io/recommend?cat=${comic.genre}` },
      { "@type": "ListItem", position: 3, name: comic.title, item: `https://bencomic.github.io/comic/${comic.id}` },
    ],
  };

  const comicJsonLd = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: comic.title,
    author: { "@type": "Person", name: comic.author },
    description: comic.desc,
    numberOfPages: comic.chapters,
    genre: comic.genre,
    image: comic.cover,
    inLanguage: "zh-CN",
  };

  const chapterGroups = [
    { label: "最新章节", range: (0, 6) as const },
    { label: "热门章节", range: (6, 12) as const },
    { label: "早期章节", range: (12, 18) as const },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(comicJsonLd) }} />

      <Navbar />
      <main>
        {/* 顶部简介区 */}
        <section className="relative overflow-hidden border-b border-background-300/50">
          <div className="absolute inset-0">
            <img src={comic.cover} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25 blur-sm" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/70"></div>

          <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-600 mb-8">
              <Link to="/" className="hover:text-primary-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <Link to={`/recommend?cat=${comic.genre}`} className="hover:text-primary-400 transition-colors">{comic.genre}</Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-500">{comic.title}</span>
            </nav>

            <div className="flex flex-col sm:flex-row gap-6 md:gap-10">
              <img src={comic.cover} alt={`${comic.title}漫画封面`} className="h-64 w-44 sm:h-80 sm:w-56 shrink-0 rounded-lg object-cover object-top border border-background-300/50" />
              <div className="flex-1">
                <h1 className="font-heading text-3xl md:text-5xl text-foreground-950">{comic.title}</h1>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {comic.tags.map((t) => (
                    <span key={t} className="rounded-full bg-secondary-100/70 px-3 py-1 text-xs text-secondary-900">{t}</span>
                  ))}
                </div>

                <dl className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  {[
                    { label: "评分", value: comic.rating.toFixed(1), icon: "ri-star-fill" },
                    { label: "热度", value: comic.hot >= 10000 ? `${(comic.hot / 10000).toFixed(1)}w` : `${comic.hot}`, icon: "ri-fire-fill" },
                    { label: "章节", value: `${comic.chapters}话`, icon: "ri-file-list-3-line" },
                    { label: "状态", value: comic.status, icon: "ri-refresh-line" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg bg-background-100 border border-background-300/50 p-3">
                      <dt className="flex items-center justify-center gap-1 text-xs text-foreground-600"><i className={`${s.icon} text-accent-400`}></i>{s.label}</dt>
                      <dd className="mt-1 font-heading text-lg text-foreground-950">{s.value}</dd>
                    </div>
                  ))}
                </dl>

                <p className="mt-6 text-sm md:text-base leading-relaxed text-foreground-600">{comic.desc}</p>
                <p className="mt-2 text-sm text-foreground-700">作者：{comic.author} · {comic.updateInfo}</p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link to={`/recommend?cat=${comic.genre}`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors">
                    <i className="ri-play-fill text-lg"></i>开始开刷
                  </Link>
                  <button type="button" className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-background-100 border border-background-300/60 px-7 py-3 text-sm font-semibold text-foreground-950 hover:border-primary-500/50 transition-colors">
                    <i className="ri-bookmark-line"></i>收藏到追漫
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 简介 + 章节 */}
        <section className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">作品简介</h2>
              <p className="text-sm md:text-base leading-relaxed text-foreground-600">{comic.intro}</p>

              <h2 className="mt-10 font-heading text-xl md:text-2xl text-foreground-950 mb-4">章节列表</h2>
              <div className="flex flex-col gap-6">
                {chapterGroups.map((g) => (
                  <div key={g.label}>
                    <h3 className="text-sm font-semibold text-foreground-600 mb-3">{g.label}</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {Array.from({ length: 6 }).map((_, i) => {
                        const num = comic.chapters - g.range[0] - i;
                        if (num <= 0) return null;
                        return (
                          <li key={i}>
                            <Link
                              to={`/comic/${comic.id}`}
                              className="flex items-center justify-between gap-2 rounded-lg bg-background-100 border border-background-300/40 px-4 py-2.5 text-sm text-foreground-700 hover:border-primary-500/50 hover:text-primary-400 transition-colors"
                            >
                              <span className="truncate">第 {num} 话 · 剧情高潮推进</span>
                              <i className="ri-play-circle-line text-primary-400 shrink-0"></i>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* 侧栏 */}
            <aside className="space-y-6">
              <div className="rounded-lg bg-background-100 border border-background-300/50 p-5">
                <h3 className="font-heading text-lg text-foreground-950 mb-4">作品信息</h3>
                <ul className="space-y-3 text-sm text-foreground-600">
                  <li className="flex justify-between"><span>作者</span><span className="text-foreground-950">{comic.author}</span></li>
                  <li className="flex justify-between"><span>类型</span><span className="text-foreground-950">{comic.genre}</span></li>
                  <li className="flex justify-between"><span>状态</span><span className="text-primary-400">{comic.status}</span></li>
                  <li className="flex justify-between"><span>章节</span><span className="text-foreground-950">{comic.chapters}话</span></li>
                  <li className="flex justify-between"><span>评分</span><span className="text-accent-400">{comic.rating.toFixed(1)}</span></li>
                </ul>
              </div>
              <div className="rounded-lg bg-primary-500/10 border border-primary-500/30 p-5">
                <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                  <i className="ri-flashlight-fill text-primary-400"></i>追漫小贴士
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  喜欢《{comic.title}》这种{comic.genre}的话，不妨到爽漫推荐页逛逛同类合集，把同类型的热门开挂主角漫画一网打尽。
                </p>
                <Link to={`/recommend?cat=${comic.genre}`} className="mt-4 inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-500 transition-colors">
                  看看同类合集<i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>

          {/* 相关推荐 */}
          <div className="mt-14">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">相关推荐 · 追完这部接着刷</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}